import React from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import { SR_API_ACTION_UTILITY_PARTNER_UPDATE_PROFILE } from 'api/utilityActionTypes';

import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import {
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import UpSelfOnboardingHeader from 'client/components/UPSelfOnboarding/UpSelfOnboardingHeader';

import './UpSelfOnboarding.less';

const getFormFields = ({ isCircleCI }) => {
  const formFields = [
    hideFieldIf(isCircleCI === 'true', {
      id: 'logoUrl',
      title: 'Company Logo',
      type: 'file_single',
      subtitle: 'JPG or PNG.',
      properties: {
        placeholder: 'Tap to upload',
        fileAccept: 'image/*',
        filePath: '/partner-uploads/logos/{uuid}',
        className: 'selfOnboardingLogoUpload selfOnboardingSubtitle',
      },
      validations: ['required'],
    }),
  ];
  return formFields;
};

const UpSelfOnboardingBrandStep = ({
  utilityPartner,
  utilityPartnerGroup,
  activeIndex,
  onNext,
  onNavigateToStep,
}) => {
  const dispatch = useDispatch();
  const theme = utilityPartnerGroup?.defaultTheme || {};

  const { isCircleCI } = useBranch({
    isCircleCI: ['env', 'CIRCLECI'],
  });

  const formFields = getFormFields({
    isCircleCI: isCircleCI,
  });

  const onSubmit = (formValues) => {
    if (!formFields.length) {
      return onNext();
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_UPDATE_PROFILE,
      payload: {
        utilityPartnerId: utilityPartner.id,
        updates: {
          logoUrl: formValues.logoUrl,
        },
      },
      onSuccess: () => {
        onNext();
      },
    });
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: formFields,
      })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        return (
          <div styleName="layout">
            <div styleName="left">
              <UpSelfOnboardingHeader
                activeIndex={activeIndex}
                onNavigateToStep={onNavigateToStep}
                partnerGroupLogoUrl={utilityPartnerGroup?.logoUrl}
                partnerGroupPrimaryColor={theme.primaryColor}
                partnerGroupSecondaryColor={theme.secondaryColor}
                partnerGroupInactiveColor={theme.inactiveTextColor}
              />
              <div styleName="lineSeparator" />
              <Form styleName="form">
                <span styleName="titleText">Brand it your own.</span>
                <SrBox margin="26px 0">
                  {formFields.map((formField, index) => (
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                      margin={index === 0 ? '0' : null}
                    />
                  ))}
                </SrBox>
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  hasBoxShadow
                  type="submit"
                  themeOverrides={{
                    backgroundColor: theme?.buttonPrimaryColor,
                    color: theme?.buttonPrimaryTextColor,
                  }}
                >
                  Continue
                </SrButton>
              </Form>
            </div>
            <div styleName="right">
              <div styleName="content brandStep fadeIn">
                <div styleName="row">
                  <span styleName="titleText">Tenant view (Search):</span>
                  <img
                    src="/images/up-self-onboarding-brand-address-search-page.png"
                    alt="Address Search"
                    styleName="addressSearchImg"
                  />
                </div>
                <div styleName="row">
                  <span styleName="titleText">Tenant view (set up utilities):</span>
                  <img
                    src="/images/up-self-onboarding-brand-utility-activation-page.png"
                    alt="Utility Activation"
                    styleName="utilityActivationImg"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

UpSelfOnboardingBrandStep.displayName = 'UpSelfOnboardingBrandStep';

UpSelfOnboardingBrandStep.propTypes = {
  utilityPartnerGroup: PropTypes.object,
  utilityPartner: PropTypes.object,
  activeIndex: PropTypes.number,
  onNext: PropTypes.func.isRequired,
  onNavigateToStep: PropTypes.func,
};

export default UpSelfOnboardingBrandStep;
